import {
	bulkCreateMipILoansUploadFile,
	getMipLoansData,
} from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { asyncLoadExceljs } from '@dealstack/frontend/shared/utils/asyncLoadExceljs';
import { EXCEL_SPREADSHEET_TYPE } from '@dealstack/shared/lib/common/business/excel';
import { getLoanDefinitionTypeNameFromType } from '@dealstack/shared/lib/common/business/loans';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef, getCoreRowModel, Table, useReactTable } from '@tanstack/react-table';
import { saveAs } from 'file-saver';
import { RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { useVirtual } from 'react-virtual';
import type { RowVirtualizer } from '@dealstack/frontend/shared/utils/reactVirtualTypes';
import type { LoansTableItemType } from './useLoansManagementController.types';

export const useLoansManagementController = (mipId: string) => {
	const toastApi = useDSToast();

	const [showBulkEditModal, setShowBulkEditModal] = useState(false);

	const getMipInvestmentsDataRequest = useQuery({
		queryKey: [API_PATHS.GODMODE_MIP_MIPID_LOANS_GET, { mipId }],
		queryFn: ({ signal }) =>
			getMipLoansData(API_PATHS.GODMODE_MIP_MIPID_LOANS_GET, { mipId }, signal),
	});

	const bulkCreateUpload = useWrappedRequest(
		bulkCreateMipILoansUploadFile(mipId),
		{},
		{
			onSuccessDefault: () => {
				void getMipInvestmentsDataRequest.refetch();
				setShowBulkEditModal(false);
				toastApi.success({});
			},
			onLocalErrorDefault: ({ error }) => {
				const message = error?.code ? error.message : 'Error during uploading file';
				toastApi.error({ message });
				logger.error(`Loans create ${error}`);
			},
		}
	);

	const loansListData = useMemo((): LoansTableItemType[] => {
		const loanDefinitions = getMipInvestmentsDataRequest?.data?.payload;

		const loans = loanDefinitions
			?.map((loanDefinition) => {
				const loans = loanDefinition.loans;

				return loans?.map((loan) => {
					return {
						loanDefinition,
						loan,
					};
				});
			})
			.flat();

		return loans ?? [];
	}, [getMipInvestmentsDataRequest?.data?.payload]);

	const manageLoansListColumns: ColumnDef<LoansTableItemType>[] = useMemo(() => {
		return [
			{
				id: 'loanId',
				accessorFn: (row) => row?.loan.id,
				size: 50,
				header: () => <span className="support-app-compact-text">Loan Id</span>,
				cell: (info) => {
					const loanId = info.row.original.loan.id;
					return <span className="support-app-compact-text">{loanId}</span>;
				},
			},
			{
				id: 'investmentWorkflowId',
				accessorFn: (row) => row?.loan.investmentWorkflowId,
				size: 50,
				header: () => (
					<span className="support-app-compact-text">InvestmentWorkflow Id</span>
				),
				cell: (info) => {
					const investmentWorkflowId = info.row.original.loan.investmentWorkflowId;
					return <span className="support-app-compact-text">{investmentWorkflowId}</span>;
				},
			},
			{
				id: 'type',
				accessorFn: (row) => row?.loanDefinition.type,
				size: 30,
				header: () => <span className="support-app-compact-text">Type</span>,
				cell: (info) => {
					const type = getLoanDefinitionTypeNameFromType(
						info.row.original.loanDefinition.type
					);
					return <span className="support-app-compact-text">{type}</span>;
				},
			},
			{
				id: 'name',
				accessorFn: (row) => row?.loanDefinition.name,
				size: 30,
				header: () => <span className="support-app-compact-text">Name</span>,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info.row.original.loanDefinition.name}
						</span>
					);
				},
			},
			{
				id: 'funder',
				accessorFn: (row) => row?.loanDefinition.funder,
				size: 30,
				header: () => <span className="support-app-compact-text">Funder</span>,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info.row.original.loanDefinition.funder}
						</span>
					);
				},
			},
			{
				id: 'amount',
				accessorFn: (row) => row?.loan.amount,
				maxSize: 10,
				header: () => <span className="support-app-compact-text">Amount</span>,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info.row.original.loan.amount}
						</span>
					);
				},
			},
			{
				id: 'amountRemaining',
				accessorFn: (row) => row?.loan.amountRemaining,
				maxSize: 20,
				header: () => <span className="support-app-compact-text">Amount remaining</span>,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info.row.original.loan.amountRemaining}
						</span>
					);
				},
			},
		];
	}, []);

	const tanstackTable: Table<LoansTableItemType> = useReactTable({
		data: loansListData,
		columns: manageLoansListColumns,
		getCoreRowModel: getCoreRowModel(),
	});

	const tableContainerRef: RefObject<HTMLTableElement> = useRef<HTMLTableElement>(null);

	const { rows } = tanstackTable.getRowModel();

	const rowVirtualizer: RowVirtualizer = useVirtual({
		parentRef: tableContainerRef,
		size: rows.length,
		overscan: 10,
	});

	const onRefreshTable = async (): Promise<void> => {
		await getMipInvestmentsDataRequest.refetch();
	};

	const onCloseModal = useCallback(() => setShowBulkEditModal(false), []);

	const onOpenModal = useCallback(() => setShowBulkEditModal(true), [showBulkEditModal]);

	const onDownloadXLSXTemplate = async (): Promise<void> => {
		const ExcelJS = await asyncLoadExceljs();
		const workbook = new ExcelJS.Workbook();
		workbook.created = new Date();

		const dataWorksheet = workbook.addWorksheet(`bulk-update-loans`);

		const columnsName = [
			'investmentWorkflowId(existing)',
			'loanId',
			'loan type',
			'loan name',
			'loan funder',
			'loan amount',
			'loan amount remaining',
		];

		const columnWidths = {
			'investmentWorkflowId(existing)': 40,
			loanId: 40,
			'loan type': 25,
			'loan name': 30,
			'loan funder': 30,
			'loan amount': 25,
			'loan amount remaining': 30,
		};

		dataWorksheet.columns = columnsName.map((name) => ({
			header: name,
			key: name,
			width: columnWidths[name] || 20,
		}));

		const spreadsheetData = loansListData.map((item) => ({
			'investmentWorkflowId(existing)': item.loan.investmentWorkflowId,
			loanId: item.loan.id,
			'loan type': getLoanDefinitionTypeNameFromType(item.loanDefinition.type),
			'loan name': item.loanDefinition.name,
			'loan funder': item.loanDefinition.funder,
			'loan amount': item.loan.amount,
			'loan amount remaining': item.loan.amountRemaining,
		}));

		dataWorksheet.addRows(spreadsheetData);

		const xls64 = await workbook.xlsx.writeBuffer();

		saveAs(
			new Blob([xls64], {
				type: EXCEL_SPREADSHEET_TYPE,
			}),
			`update-loans-management-mip-${mipId}.xlsx`
		);
	};

	return {
		rowVirtualizer,
		tableContainerRef,
		rows,
		onRefreshTable,
		tanstackTable,
		isLoading: getMipInvestmentsDataRequest.isLoading,
		getMipInvestmentsDataRequest,
		showBulkEditModal,
		setShowBulkEditModal,
		onDownloadXLSXTemplate,
		onCloseModal,
		onOpenModal,
		bulkCreateUpload,
	};
};
